import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Trophy, Clock, CheckCircle, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import { formatNumber, timeUntil } from '../lib/utils';
import { useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { parseEther, formatUnits } from 'viem';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DISTRIBUTOR_ABI = [
  {
    inputs: [
      { name: 'wallet', type: 'address' },
      { name: 'points', type: 'uint256' },
      { name: 'proof', type: 'bytes32[]' }
    ],
    name: 'claim',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  }
];

// Format BNB from wei string (PURE STRING - no float conversion)
const formatBnbAmount = (weiString, fallbackString) => {
  let decimalStr;
  
  if (weiString) {
    decimalStr = formatUnits(BigInt(weiString), 18);
  } else if (fallbackString) {
    decimalStr = fallbackString;
  } else {
    return '0.000000';
  }
  
  // Pure string manipulation to get exactly 6 decimal places
  const parts = decimalStr.split('.');
  const intPart = parts[0] || '0';
  let decPart = parts[1] || '';
  
  if (decPart.length < 6) {
    decPart = decPart.padEnd(6, '0');
  } else if (decPart.length > 6) {
    decPart = decPart.slice(0, 6);
  }
  
  return `${intPart}.${decPart}`;
};

export const ClaimCard = ({ epoch, reward, wallet, onClaimSuccess }) => {
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState(null);
  
  // Check if distributor is funded
  const isFunded = reward?.isFunded === true;
  
  const { 
    writeContract, 
    data: hash, 
    isPending: isWritePending,
    isError: isWriteError,
    error: writeError,
    reset: resetWrite
  } = useWriteContract();
  
  const { 
    isLoading: isConfirming, 
    isSuccess,
    isError: isConfirmError 
  } = useWaitForTransactionReceipt({ hash });
  
  useEffect(() => {
    if (isWriteError && writeError) {
      const errorMsg = writeError.shortMessage || writeError.message || 'Transaction failed';
      setError(errorMsg);
      toast.error(errorMsg);
      setClaiming(false);
      resetWrite();
    }
  }, [isWriteError, writeError, resetWrite]);

  useEffect(() => {
    if (isConfirmError) {
      setError('Transaction failed on chain');
      toast.error('Transaction failed on chain');
      setClaiming(false);
      resetWrite();
    }
  }, [isConfirmError, resetWrite]);
  
  useEffect(() => {
    if (isSuccess && hash) {
      axios.post(`${BACKEND_URL}/api/claim/update`, {
        epochId: epoch.epochId,
        wallet,
        tx_hash: hash
      }).then((response) => {
        // Handle pending receipt case
        if (response.data.status === 'pending') {
          toast.info('Claim confirmed on-chain. Syncing...');
        } else {
          toast.success('Rewards claimed successfully!');
        }
        setClaiming(false);
        setError(null);
        if (onClaimSuccess) onClaimSuccess();
      }).catch(err => {
        toast.success('Rewards claimed! (Refresh to update status)');
        setClaiming(false);
      });
    }
  }, [isSuccess, hash, epoch.epochId, wallet, onClaimSuccess]);
  
  const handleClaim = async () => {
    try {
      setClaiming(true);
      setError(null);
      
      const response = await axios.get(`${BACKEND_URL}/api/proof/${epoch.epochId}/${wallet}`);
      const { amount, proof } = response.data;
      
      if (!epoch.distributorAddress) {
        throw new Error('Distributor not deployed yet');
      }
      
      // Amount is already in wei format from the API
      const amountBigInt = BigInt(amount);
      
      writeContract({
        address: epoch.distributorAddress,
        abi: DISTRIBUTOR_ABI,
        functionName: 'claim',
        args: [wallet, amountBigInt, proof]
      });
      
      toast.info('Please confirm in your wallet...');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to claim';
      setError(errorMsg);
      toast.error(errorMsg);
      setClaiming(false);
    }
  };
  
  const isPending = claiming || isWritePending || isConfirming;
  const isExpired = epoch.claimDeadline && new Date(epoch.claimDeadline) < new Date();
  
  return (
    <div className={`rounded-xl p-4 border ${
      reward.claimed 
        ? 'bg-green-50 border-green-200' 
        : 'bg-white border-[#e0dbd3]'
    }`} data-testid="claim-card">
      
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="w-4 h-4 text-[#f0b90b]" />
            <span className="font-semibold text-[#1a1a1a]">Epoch {epoch.epochId}</span>
            
            {reward.claimed ? (
              <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                <CheckCircle className="w-3 h-3" />
                Claimed
              </span>
            ) : isExpired ? (
              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                Expired
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs bg-[#f0b90b]/10 text-[#b8860b] px-2 py-0.5 rounded-full">
                <Clock className="w-3 h-3" />
                {timeUntil(epoch.claimDeadline)}
              </span>
            )}
          </div>
          
          <div className="text-xs text-[#888] mb-2">
            {new Date(epoch.startTime).toLocaleDateString()} - {new Date(epoch.endTime).toLocaleDateString()}
          </div>
          
          <div className="flex gap-4 text-sm flex-wrap">
            <div>
              <span className="text-[#888]">Tipper: </span>
              <span className="font-medium text-[#333]">{formatNumber(reward.tipperPoints)} pts</span>
            </div>
            <div>
              <span className="text-[#888]">Creator: </span>
              <span className="font-medium text-[#333]">{formatNumber(reward.creatorPoints)} pts</span>
            </div>
            {parseFloat(reward.scoutPoints || 0) > 0 && (
              <div>
                <span className="text-[#888]">Scout: </span>
                <span className="font-medium text-[#333]">{formatNumber(reward.scoutPoints)} pts</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-2xl font-bold text-[#f0b90b]">
            {reward.claimed && !reward.claimedVerified ? (
              // Claimed but not yet verified - show syncing state
              <span className="text-lg">
                ≈ {formatBnbAmount(null, reward.totalReward)}
                <span className="text-xs text-[#888] ml-1">(syncing)</span>
              </span>
            ) : (
              // Verified or unclaimed - use precision-safe format
              formatBnbAmount(reward.totalRewardWei, reward.totalReward)
            )}
          </div>
          <div className="text-xs text-[#888]">BNB</div>
        </div>
      </div>
      
      {error && (
        <div className="mt-3 p-2 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-xs text-red-600">{error}</p>
        </div>
      )}
      
      {/* Not Funded Warning */}
      {!reward.claimed && !isExpired && !isFunded && (
        <div className="mt-3 p-2 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-2">
          <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <p className="text-xs text-amber-700">Awaiting funding. Claims will be enabled once the reward pool is funded.</p>
        </div>
      )}
      
      {hash && !isSuccess && (
        <div className="mt-3 p-2 rounded-lg bg-blue-50 border border-blue-200">
          <p className="text-xs text-blue-600 flex items-center gap-2">
            <span>TX: {hash.slice(0, 10)}...{hash.slice(-6)}</span>
            <a 
              href={`https://bscscan.com/tx/${hash}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 underline"
            >
              View <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>
      )}
      
      {!reward.claimed && !isExpired && (
        <Button 
          onClick={handleClaim}
          disabled={isPending || !isFunded}
          className={`w-full mt-4 font-medium rounded-xl ${
            isFunded 
              ? 'bg-[#f0b90b] hover:bg-[#d4a50a] text-white' 
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          data-testid="claim-button"
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {isConfirming ? 'Confirming...' : isWritePending ? 'Confirm in wallet...' : 'Processing...'}
            </>
          ) : isFunded ? (
            'Claim Rewards'
          ) : (
            'Awaiting Funding'
          )}
        </Button>
      )}
    </div>
  );
};
